import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiGet, apiPost, apiUpload } from '../../services/api'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import { saveOfflineSubmission, saveOfflineAttachment } from '../../services/offline'
import {
  ArrowLeft,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
  Trash2,
  MapPin,
  Calendar,
  Loader2,
  Plus,
  File,
} from 'lucide-react'
import LoadingSpinner from '../../components/common/LoadingSpinner'

interface Indicator {
  id: number
  code: string
  name: string
  target: number
  max_points: number
  points_per_unit: number
}

interface Period {
  id: number
  year: number
  month: number
  name?: string
  is_locked: boolean
}

interface Submission {
  id: number
  submission_date: string
  status: string
  notes?: string
  created_at: string
}

interface LocationData {
  latitude: number
  longitude: number
  accuracy: number
  address?: string
}

interface DocumentEntry {
  id: string
  file: File
  name: string
  size: number
}

const MAX_DOCUMENTS = 10

export default function C5SubmissionPage() {
  const navigate = useNavigate()
  const isOnline = useOnlineStatus()
  const documentInputRef = useRef<HTMLInputElement>(null)

  const [indicator, setIndicator] = useState<Indicator | null>(null)
  const [activePeriod, setActivePeriod] = useState<Period | null>(null)
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [submissionCount, setSubmissionCount] = useState(0)
  const [calculatedPoints, setCalculatedPoints] = useState(0)

  const [documents, setDocuments] = useState<DocumentEntry[]>([])
  const [notes, setNotes] = useState('')

  const [location, setLocation] = useState<LocationData | null>(null)
  const [locationLoading, setLocationLoading] = useState(false)

  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const currentDate = new Date()
  const formattedDate = currentDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  const submissionDate = currentDate.toISOString().split('T')[0]

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (activePeriod && indicator) {
      fetchSubmissions()
    }
  }, [activePeriod, indicator])

  const fetchData = async () => {
    try {
      const indicators = await apiGet<Indicator[]>('/kpi/indicators', { is_active: true })
      const c5Indicator = indicators.find(i => i.code === 'C.5')
      if (c5Indicator) {
        setIndicator(c5Indicator)
      }

      const periodsData = await apiGet<Period[]>('/kpi/periods')
      const currentMonth = currentDate.getMonth() + 1
      const currentYear = currentDate.getFullYear()

      let matchingPeriod = periodsData.find(
        p => p.month === currentMonth && p.year === currentYear && !p.is_locked
      )
      if (!matchingPeriod) {
        matchingPeriod = periodsData.find(p => !p.is_locked) || periodsData[0]
      }
      setActivePeriod(matchingPeriod || null)
    } catch (err) {
      console.error('Failed to load data:', err)
      setError('Failed to load data. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchSubmissions = async () => {
    if (!activePeriod?.id || !indicator?.id) return

    try {
      const scoresData = await apiGet<{
        scores: Array<{ indicator: Indicator; submission_count: number; calculated_score: number }>
      }>('/kpi/scores', { period_id: activePeriod.id })

      const score = scoresData.scores.find(s => s.indicator.code === 'C.5')
      if (score) {
        setSubmissionCount(score.submission_count)
        setCalculatedPoints(score.calculated_score)
      }

      const submissionsData = await apiGet<{ items: Submission[] }>('/kpi/submissions', {
        period_id: activePeriod.id,
        indicator_id: indicator.id,
        per_page: 10,
      })
      setSubmissions(submissionsData.items || [])
    } catch (err) {
      console.error('Failed to load submissions:', err)
    }
  }

  const fetchLocation = async (): Promise<LocationData | null> => {
    setLocationLoading(true)

    if (!navigator.geolocation) {
      setLocationLoading(false)
      return null
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const locationData: LocationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          }

          if (isOnline) {
            try {
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}`
              )
              const data = await response.json()
              if (data.display_name) {
                locationData.address = data.display_name
              }
            } catch (err) {
              console.error('Failed to get address:', err)
            }
          }

          setLocation(locationData)
          setLocationLoading(false)
          resolve(locationData)
        },
        () => {
          setLocationLoading(false)
          resolve(null)
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      )
    })
  }

  const handleDocumentSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files
    if (!selectedFiles) return

    const newDocuments: DocumentEntry[] = []
    const remainingSlots = MAX_DOCUMENTS - documents.length

    for (let i = 0; i < Math.min(selectedFiles.length, remainingSlots); i++) {
      const file = selectedFiles[i]
      if (file.size > 16 * 1024 * 1024) {
        setError(`File "${file.name}" exceeds 16MB limit`)
        continue
      }

      newDocuments.push({
        id: `doc_${Date.now()}_${i}`,
        file,
        name: file.name,
        size: file.size,
      })
    }

    if (newDocuments.length > 0) {
      setDocuments([...documents, ...newDocuments])
      setError(null)
    }

    if (documentInputRef.current) {
      documentInputRef.current.value = ''
    }
  }

  const removeDocument = (docId: string) => {
    setDocuments(documents.filter(d => d.id !== docId))
  }

  const handleSubmit = async () => {
    if (!activePeriod || !indicator) {
      setError('No active period found')
      return
    }

    if (documents.length === 0) {
      setError('Please upload at least one document')
      return
    }

    setIsSubmitting(true)
    setError(null)

    const submissionLocation = await fetchLocation()

    const locationNote = submissionLocation
      ? `Location: ${submissionLocation.latitude.toFixed(6)}, ${submissionLocation.longitude.toFixed(6)}${submissionLocation.address ? ` (${submissionLocation.address})` : ''}`
      : 'Location: Not available'
    const documentCount = `Documents uploaded: ${documents.length}`
    const fullNotes = notes
      ? `${notes}\n\n${documentCount}\n${locationNote}`
      : `${documentCount}\n${locationNote}`

    try {
      if (isOnline) {
        const response = await apiPost<{ id: number }>('/kpi/submissions', {
          period_id: activePeriod.id,
          indicator_id: indicator.id,
          submission_date: submissionDate,
          value: 1,
          notes: fullNotes,
          metadata: {
            location: submissionLocation ? {
              latitude: submissionLocation.latitude,
              longitude: submissionLocation.longitude,
              accuracy: submissionLocation.accuracy,
              address: submissionLocation.address,
            } : null,
            captured_at: currentDate.toISOString(),
            document_count: documents.length,
          },
        })

        if (response.id) {
          for (let i = 0; i < documents.length; i++) {
            const doc = documents[i]
            const formData = new FormData()
            formData.append('file', doc.file)
            formData.append('submission_id', String(response.id))
            formData.append('file_type', `monthly_report_${i + 1}`)
            await apiUpload('/kpi/attachments', formData)
          }
        }
      } else {
        const offlineNotes = `${fullNotes}\n\nCaptured at: ${currentDate.toISOString()}`

        const clientId = await saveOfflineSubmission({
          client_id: `c5_${Date.now()}`,
          period_id: activePeriod.id,
          indicator_id: indicator.id,
          submission_date: submissionDate,
          value: 1,
          notes: offlineNotes,
          created_at: new Date().toISOString(),
        })

        for (const doc of documents) {
          await saveOfflineAttachment(clientId, doc.file, doc.name, doc.file.type)
        }
      }

      setSuccess(true)

      setTimeout(() => {
        setDocuments([])
        setNotes('')
        setLocation(null)
        setSuccess(false)
        fetchSubmissions()
      }, 2000)
    } catch (err) {
      console.error('Submission failed:', err)
      setError('Failed to submit. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="mobile-page flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const progress = indicator ? Math.min((submissionCount / indicator.target) * 100, 100) : 0

  return (
    <div className="mobile-page bg-gray-50">
      <input
        ref={documentInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
        multiple
        onChange={handleDocumentSelect}
      />

      {/* Header */}
      <div className="bg-pink-500 px-4 pt-4 pb-4">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate('/kpi')} className="mobile-back-btn">
            <ArrowLeft className="h-6 w-6 text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-white text-lg font-semibold">Monthly Report</h1>
            <p className="text-white/80 text-xs">C.5 - Monthly Documentation</p>
          </div>
        </div>

        {/* Progress Card */}
        <div className="bg-white rounded-xl shadow-lg p-4">
          <div className="flex items-center gap-4">
            <div className="bg-pink-100 rounded-xl p-3">
              <FileText className="h-8 w-8 text-pink-500" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-gray-900">
                  {submissionCount} / {indicator?.target || 1}
                </span>
                <span className="text-sm font-semibold text-pink-500">
                  {calculatedPoints.toFixed(2)} pts
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Max {indicator?.max_points || 15} points
              </p>
              <div className="mt-2 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-pink-500 rounded-full h-2 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="px-4 mt-4 pb-32">
        {/* Auto-captured Date */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-pink-100 rounded-lg p-2">
              <Calendar className="h-5 w-5 text-pink-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Submission Date (Auto)</p>
              <p className="font-medium text-gray-900">{formattedDate}</p>
            </div>
          </div>
        </div>

        {/* Location Status */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center gap-3">
            <div className={`rounded-lg p-2 ${location ? 'bg-green-100' : 'bg-gray-100'}`}>
              {locationLoading ? (
                <Loader2 className="h-5 w-5 text-gray-500 animate-spin" />
              ) : (
                <MapPin className={`h-5 w-5 ${location ? 'text-green-500' : 'text-gray-400'}`} />
              )}
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500">Location (Captured on Submit)</p>
              {locationLoading ? (
                <p className="text-sm text-gray-600">Getting location...</p>
              ) : location ? (
                <div>
                  <p className="font-medium text-gray-900 text-sm">
                    {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                  </p>
                  {location.address && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{location.address}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Will be captured when you submit</p>
              )}
            </div>
          </div>
        </div>

        {/* Documents Upload Section */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Monthly Report Documents
              <span className="text-red-500">*</span>
            </label>
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
              documents.length >= MAX_DOCUMENTS ? 'bg-red-100 text-red-600' : 'bg-pink-100 text-pink-600'
            }`}>
              {documents.length} / {MAX_DOCUMENTS}
            </span>
          </div>

          {documents.length > 0 && (
            <div className="space-y-2 mb-3">
              {documents.map((doc, index) => (
                <div key={doc.id} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                  <div className="bg-pink-100 rounded-lg p-2">
                    <File className="h-5 w-5 text-pink-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {index + 1}. {doc.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(doc.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    onClick={() => removeDocument(doc.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {documents.length < MAX_DOCUMENTS && (
            <button
              onClick={() => documentInputRef.current?.click()}
              className="mobile-upload-area"
            >
              <Plus className="h-8 w-8 text-gray-400 mb-2" />
              <span className="text-gray-600 font-medium">
                {documents.length === 0 ? 'Upload Monthly Report' : 'Add More Documents'}
              </span>
              <span className="text-gray-400 text-xs mt-1">
                PDF, Word, Excel or Image (max 16MB each)
              </span>
              <span className="text-pink-600 text-xs mt-1 font-medium">
                {MAX_DOCUMENTS - documents.length} slots remaining
              </span>
            </button>
          )}

          {documents.length >= MAX_DOCUMENTS && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
              <p className="text-sm text-yellow-700">Maximum {MAX_DOCUMENTS} documents reached</p>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about the monthly report..."
            rows={3}
            className="mobile-textarea"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <p className="text-sm text-green-700">
              {isOnline ? 'Monthly Report submitted successfully!' : 'Saved offline. Will sync when online.'}
            </p>
          </div>
        )}

        {/* Offline Warning */}
        {!isOnline && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
            <p className="text-sm text-yellow-700">
              You're offline. Submission will be saved locally and synced later.
            </p>
          </div>
        )}

        {/* Recent Submissions */}
        {submissions.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Recent Submissions
            </h3>
            <div className="space-y-2">
              {submissions.slice(0, 5).map((sub) => (
                <div key={sub.id} className="bg-white rounded-xl p-3 shadow-sm flex items-center gap-3">
                  <div className="bg-pink-100 rounded-lg p-2">
                    <FileText className="h-5 w-5 text-pink-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(sub.submission_date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                      })}
                    </p>
                    {sub.notes && (
                      <p className="text-xs text-gray-500 truncate">{sub.notes.split('\n')[0]}</p>
                    )}
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    sub.status === 'approved' ? 'bg-green-100 text-green-700' :
                    sub.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {sub.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="mobile-submit-container">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || documents.length === 0}
          className="mobile-submit-btn bg-pink-500 hover:bg-pink-600 disabled:bg-gray-300"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Submitting...
            </span>
          ) : (
            `Submit C.5 (${documents.length} ${documents.length === 1 ? 'document' : 'documents'})`
          )}
        </button>
      </div>
    </div>
  )
}
